import { useEffect, useRef } from 'react';

import styles from './Modal.module.css';

/**
 * The dialog shell: backdrop, panel, and the keyboard behaviour every dialog owes
 * the user.
 *
 * Extracted because there are three of them now — confirm, schedule, import — and
 * a focus trap copied three times is a focus trap that will differ three ways
 * within a month. The mock's `.modal-bg` / `.modal` / `.modal-head` / `.modal-foot`
 * layout lives here too, so the dialogs cannot drift apart visually either.
 *
 * What the mock has none of, and this adds: Escape closes, the backdrop closes,
 * focus moves in on open and returns to whatever opened it on close, and Tab is
 * trapped inside. A dialog you cannot leave from the keyboard is a dead end.
 */
export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  labelledBy = 'modal-title',
}: {
  open: boolean;
  title: string;
  subtitle?: string | undefined;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
  labelledBy?: string;
}): JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;

    opener.current = document.activeElement;

    // The first focusable thing inside, which for a form is its first field and
    // for a confirmation is its Cancel button — see ConfirmDialog for why that
    // ordering is deliberate there.
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      // Without trapping, Tab walks off into the page behind the backdrop, where
      // clicks do nothing and nothing looks focused.
      const focusable = panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable || focusable.length === 0) return;
      const head = focusable[0];
      const tail = focusable[focusable.length - 1];
      if (!head || !tail) return;

      if (event.shiftKey && document.activeElement === head) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && document.activeElement === tail) {
        event.preventDefault();
        head.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Matters most when the control that opened this has since been removed
      // from the DOM: without it, focus falls back to <body>.
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.backdrop}
      // Both press and release on the backdrop, so a drag that ends outside the
      // panel does not dismiss it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.panel}
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        <div className={styles.head}>
          <h3 id={labelledBy}>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className={styles.body}>{children}</div>
        <div className={styles.foot}>{footer}</div>
      </div>
    </div>
  );
}

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
